import React, { useState } from 'react';
import { Modal, Input, Button, Typography, Alert, Space, Progress } from 'antd';
import {
  LockOutlined, EyeInvisibleOutlined, EyeTwoTone,
  CheckCircleOutlined, CloseCircleOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text, Title } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ═══════════════════════════════════════════
   PASSWORD STRENGTH CHECKER
   ═══════════════════════════════════════════ */

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '#d9d9d9' };

  let score = 0;
  if (password.length >= 6) score += 20;
  if (password.length >= 8) score += 15;
  if (password.length >= 12) score += 10;
  if (/[a-z]/.test(password)) score += 10;
  if (/[A-Z]/.test(password)) score += 15;
  if (/[0-9]/.test(password)) score += 15;
  if (/[^a-zA-Z0-9]/.test(password)) score += 15;

  if (score < 30) return { score, label: 'Weak', color: '#ff4d4f' };
  if (score < 55) return { score, label: 'Fair', color: '#faad14' };
  if (score < 75) return { score, label: 'Good', color: '#52c41a' };
  return { score: Math.min(score, 100), label: 'Strong', color: '#1a6ed8' };
};


/* ═══════════════════════════════════════════
   VALIDATION RULES DISPLAY
   ═══════════════════════════════════════════ */

const ValidationRule = ({ passed, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
    {passed
      ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
      : <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: 12 }} />}
    <Text style={{ fontSize: 12, color: passed ? '#389e0d' : '#8dabc4' }}>{label}</Text>
  </div>
);


/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */

const ChangePasswordModal = ({ open, onClose, username }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const strength = getPasswordStrength(newPassword);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const passwordsMismatch = confirmPassword && newPassword !== confirmPassword;

  const isValid =
    currentPassword.length > 0 &&
    newPassword.length >= 6 &&
    newPassword === confirmPassword &&
    currentPassword !== newPassword;

  const handleSubmit = async () => {
    if (!isValid) return;

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/change-password`, {
        username: username,
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      if (res.data.success) {
        setSuccess(true);
        setError('');
        // Auto-close after 2 seconds on success
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(res.data.message || 'Failed to change password');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to change password';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={460}
      destroyOnClose
      centered
      styles={{
        body: { padding: 0 },
      }}
    >
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0a3d7a 0%, #1a6ed8 60%, #4a9eff 100%)',
        borderRadius: '8px 8px 0 0',
        padding: '24px 28px 20px',
        color: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'rgba(255,255,255,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
          }}>
            <SafetyCertificateOutlined />
          </div>
          <div>
            <Title level={4} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: 18 }}>
              Change Password
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
              {username}
            </Text>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '24px 28px 20px' }}>

        {/* Success Message */}
        {success && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="Password Changed Successfully"
            description="Your password has been updated. Use your new password next time you log in."
            style={{ borderRadius: 10, marginBottom: 20 }}
          />
        )}

        {/* Error Message */}
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            closable
            onClose={() => setError('')}
            style={{ borderRadius: 10, marginBottom: 20 }}
          />
        )}

        {!success && (
          <>
            {/* Current Password */}
            <div style={{ marginBottom: 18 }}>
              <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block', marginBottom: 6 }}>
                Current Password
              </Text>
              <Input.Password
                size="large"
                placeholder="Enter your current password"
                prefix={<LockOutlined style={{ color: '#8dabc4' }} />}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setError(''); }}
                style={{ borderRadius: 10 }}
              />
            </div>

            {/* New Password */}
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block', marginBottom: 6 }}>
                New Password
              </Text>
              <Input.Password
                size="large"
                placeholder="Enter your new password"
                prefix={<LockOutlined style={{ color: '#8dabc4' }} />}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                style={{ borderRadius: 10 }}
                status={newPassword && currentPassword === newPassword ? 'error' : undefined}
              />
              {newPassword && currentPassword && currentPassword === newPassword && (
                <Text style={{ fontSize: 11, color: '#ff4d4f', display: 'block', marginTop: 4 }}>
                  New password must be different from current password
                </Text>
              )}
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Progress
                    percent={strength.score}
                    showInfo={false}
                    strokeColor={strength.color}
                    trailColor="#e8ecf1"
                    size="small"
                    style={{ flex: 1, margin: 0 }}
                  />
                  <Text style={{ fontSize: 11, color: strength.color, fontWeight: 600, minWidth: 45 }}>
                    {strength.label}
                  </Text>
                </div>

                <div style={{
                  padding: '10px 14px', background: '#f8fafc',
                  borderRadius: 8, border: '1px solid #e8ecf1',
                }}>
                  <ValidationRule passed={newPassword.length >= 6} label="At least 6 characters" />
                  <ValidationRule passed={newPassword.length >= 8} label="8+ characters (recommended)" />
                  <ValidationRule passed={/[A-Z]/.test(newPassword)} label="Contains uppercase letter" />
                  <ValidationRule passed={/[0-9]/.test(newPassword)} label="Contains a number" />
                  <ValidationRule passed={/[^a-zA-Z0-9]/.test(newPassword)} label="Contains special character" />
                </div>
              </div>
            )}

            {/* Confirm Password */}
            <div style={{ marginBottom: 24 }}>
              <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block', marginBottom: 6 }}>
                Confirm New Password
              </Text>
              <Input.Password
                size="large"
                placeholder="Re-enter your new password"
                prefix={<LockOutlined style={{ color: '#8dabc4' }} />}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                onPressEnter={isValid ? handleSubmit : undefined}
                style={{ borderRadius: 10 }}
                status={passwordsMismatch ? 'error' : passwordsMatch ? '' : undefined}
              />
              {passwordsMismatch && (
                <Text style={{ fontSize: 11, color: '#ff4d4f', display: 'block', marginTop: 4 }}>
                  Passwords do not match
                </Text>
              )}
              {passwordsMatch && (
                <Text style={{ fontSize: 11, color: '#52c41a', display: 'block', marginTop: 4 }}>
                  <CheckCircleOutlined style={{ marginRight: 4 }} /> Passwords match
                </Text>
              )}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <Button
                onClick={handleClose}
                style={{ borderRadius: 10, height: 44, flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!isValid}
                style={{
                  borderRadius: 10, height: 44, flex: 2, fontWeight: 600,
                  background: isValid ? 'linear-gradient(135deg, #1a6ed8, #4a9eff)' : '#d9d9d9',
                  border: 'none',
                }}
              >
                Change Password
              </Button>
            </div>
          </>
        )}

        {success && (
          <div style={{ textAlign: 'center', paddingBottom: 8 }}>
            <Button
              type="primary"
              onClick={handleClose}
              style={{
                borderRadius: 10, height: 44, fontWeight: 600, width: '100%',
                background: 'linear-gradient(135deg, #1a6ed8, #4a9eff)', border: 'none',
              }}
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;